import { useEffect, useState } from 'react';
import { Box, Container } from '@mui/material';
import { useDispatch } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import ReFormComponent, { type JsonSchema, type NestedObject, type UISchemaElement } from '@restore/reform';
import { useSettings } from 'src/hooks/use-settings';
import DocumentService from 'src/services/documentService';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';
import CommonLoader from 'src/utils/common-loader';
import SucessModal from '../sucess-modal';
import {
  errorForLoaderDocumentation,
  successForLoaderDocumentation,
} from 'src/redux/actions/documentationAction';
import { baseApiUrl, reFormsCompactStylesTreatment } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

type Discipline = 'PT' | 'OT' | 'SLP';

interface ReFormProps {
  formId: string;
  uuid: string;
  disablePadding?: boolean;
}

const ReForm: React.FC<ReFormProps> = (props) => {
  const { formId, uuid, disablePadding } = props;

  const settings = useSettings();
  const [autosaveDate, setAutosaveDate] = useState<Date | undefined>();
  const [schema, setSchema] = useState<JsonSchema>();
  const [uischema, setUischema] = useState<UISchemaElement>();
  const [prefill, setPrefill] = useState<NestedObject>();
  const [formStatus, setFormStatus] = useState<FormStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('Something went wrong');
  const [reFormsCompactStylesEnabled] = useFeatureToggleWhenReady(reFormsCompactStylesTreatment, 'FACILITY');
  const dispatch = useDispatch();
  const realm = getRealmName();

  const getFacilityUuid = localStorage.getItem('facilityId') || '';
  const [discipline] = useCurrentUser([
    'discipline',
  ]);

  useEffect(() => {
    if (formStatus === 'loading') {
      if (formId && realm) {
        DocumentService.getSchema(realm, formId, uuid).then((response) => {
          if(response.error) {
            setFormStatus('error');
            setErrorMessage(response.error);
          } else {
            setSchema(response.schema);
            setUischema(response.uiSchema);
            setPrefill(response.data);
            setAutosaveDate(response?.data?.metadata?.lastSavedAt || undefined);
            setFormStatus('ready');
          }
        }).catch((error) => {
          console.log('error', error)
          setFormStatus('error');
        });
      }
    }
  }, [formStatus, formId, uuid, realm]);

  let component;

  const submitCallback = (data: JsonSchema) => {
    if (formId && realm) {
      datadogRum.addAction('submitCallback_STARTED', {formId, realm}); 
      setFormStatus('submitting');
      
      DocumentService.submitReform(realm, formId, uuid, data)
        .then((success) => {
          if (success) {
            datadogRum.addAction('submitCallback_SUCCESS', {formId, realm});
            setFormStatus('success');
          } else {
            datadogRum.addAction('submitCallback_FAILED', {formId, realm});
            setFormStatus('error');
          }
        })
        .catch((error) => {
          datadogRum.addAction('submitCallback_FAILED', {formId, realm, error});
          setFormStatus('error');
        });
    }else{
      datadogRum.addAction('submitCallback_FAILED', {formId, realm});
    }
  };

  const autosaveCallback = async (data: JsonSchema): Promise<boolean> => {
    if (formId && realm) {
      datadogRum.addAction('autosaveCallback_STARTED', {formId, realm});

      return DocumentService.autosave(realm, formId, uuid, data).then((response) => {
        if(response) {
          datadogRum.addAction('autosaveCallback_SUCCESS', {formId, realm, uuid});
          return response;
        } else {
          
          datadogRum.addAction('autosaveCallback_FAILED', {formId, realm});
          return false;
        }
      });
    } else {
      return false;
    }
  };

  const handleSuccess = () => {
    dispatch(successForLoaderDocumentation('Success'));
  };

  const handleError = () => {
    dispatch(errorForLoaderDocumentation('Error'));
  };

  switch (formStatus) {
    case 'loading':
      component = <CommonLoader />;
      break;

    case 'ready':
      component = (
        <ReFormComponent
          schema={schema as JsonSchema}
          uiSchema={uischema as UISchemaElement}
          submitCallback={submitCallback}
          initialData={prefill}
          autosaveCallback={autosaveCallback}
          autosaveDate={autosaveDate}
          getAccessToken={getAccessToken}
          apiUrl={baseApiUrl}
          realm={realm || ''}
          facilityId={getFacilityUuid}
          discipline={discipline as Discipline}
          compact={reFormsCompactStylesEnabled || false}
          formUuid={uuid}
        />
      );
      break;

    case 'submitting':
      component = <CommonLoader />;
      break;

    case 'success':
      component = (
        <SucessModal
          title="Submitted"
          message="Your submission has been received"
          close={handleSuccess}
        />
      );
      break;

    default:
      component = (
        <SucessModal
          title="Error"
          message={errorMessage}
          isSucess={false}
          close={handleError}
        />
      );

      break;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: disablePadding ? 0 : 8,
      }}
    >
      <Container maxWidth={settings.stretch ? false : 'xl'}>{component}</Container>
    </Box>
  );
};

export default ReForm;
